// src/pages/DriversPage.tsx
// Drivers management page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Search,
  Filter,
  Download,
  UserCheck,
  UserX,
  CheckCircle,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Shield,
} from 'lucide-react';
import adminUserService from '../services/adminUserService';
import { User } from '../types/user';
import './DriversPage.css';
import Navigation from '../components/common/Navigation';

const DriversPage: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [drivers, setDrivers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [status, setStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Load drivers
  useEffect(() => {
    loadDrivers();
  }, [page, search, approvalStatus, status]);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch users with role 'driver' OR 'both'
      const response = await adminUserService.getUsers({
        page,
        limit: 10,
        search,
        role: '', // We'll filter on frontend to get both driver and both
        status,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      // Filter to include only drivers and "both"
      const driverUsers = response.data.filter(
        (user) => user.role === 'driver' || user.role === 'both'
      );

      setDrivers(driverUsers);
      setTotalPages(response.pagination.totalPages);
      setTotal(driverUsers.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  };

  const handleRefresh = () => {
    loadDrivers();
  };

  const handleExport = async () => {
    try {
      await adminUserService.exportUsers({ role: 'driver', status });
    } catch (err: any) {
      setError(err.message || 'Failed to export drivers');
    }
  };

  const handleViewDriver = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const getApprovalBadge = (approvalStatus: string) => {
    const styles = {
      approved: { bg: '#d1fae5', color: '#065f46', text: 'Approved' },
      pending: { bg: '#fef3c7', color: '#92400e', text: 'Pending' },
      rejected: { bg: '#fecaca', color: '#991b1b', text: 'Rejected' },
    };

    const style = styles[approvalStatus as keyof typeof styles] || styles.pending;

    return (
      <span
        className="badge"
        style={{ background: style.bg, color: style.color }}
      >
        {style.text}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: { bg: '#d1fae5', color: '#065f46' },
      suspended: { bg: '#fed7aa', color: '#92400e' },
      banned: { bg: '#fecaca', color: '#991b1b' },
    };

    const style = styles[status as keyof typeof styles] || styles.active;

    return (
      <span className="badge" style={{ background: style.bg, color: style.color }}>
        {status}
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <Navigation />
    <div className="drivers-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <Car size={32} className="header-icon" />
          <div>
            <h1>Drivers Management</h1>
            <p>Manage all drivers and approve applications</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/users')} className="btn-secondary">
            All Users
          </button>
          <button onClick={handleRefresh} className="btn-icon" title="Refresh">
            <RefreshCw size={20} />
          </button>
          <button onClick={handleExport} className="btn-secondary">
            <Download size={20} />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-label">Total Drivers</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Approved</div>
          <div className="stat-value">
            {drivers.filter((d) => d.driverProfile?.approvalStatus === 'approved').length}
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Pending</div>
          <div className="stat-value">
            {drivers.filter((d) => d.driverProfile?.approvalStatus === 'pending').length}
          </div>
        </div>
        <div className="stat-box">
          <div className="stat-label">Online</div>
          <div className="stat-value">
            {drivers.filter((d) => d.driverProfile?.isOnline).length}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search drivers..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-filter ${showFilters ? 'active' : ''}`}
        >
          <Filter size={20} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-content">
            <div className="filter-group">
              <label>Approval Status</label>
              <select
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Account Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Drivers Table */}
      {loading ? (
        <div className="table-loading">
          <div className="spinner"></div>
          <p>Loading drivers...</p>
        </div>
      ) : drivers.length === 0 ? (
        <div className="table-empty">
          <Car size={48} />
          <p>No drivers found</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="drivers-table">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Approval</th>
                <th>Status</th>
                <th>Rating</th>
                <th>Trips</th>
                <th>Online</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <tr key={driver.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        {driver.profileImage ? (
                          <img src={driver.profileImage} alt={driver.firstName} />
                        ) : (
                          <div className="avatar-placeholder">
                            {driver.firstName[0]}
                            {driver.lastName[0]}
                          </div>
                        )}
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {driver.firstName} {driver.lastName}
                        </div>
                        {driver.role === 'both' && (
                          <div className="user-meta">
                            <Shield size={12} />
                            Also a Passenger
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{driver.email}</td>
                  <td>{driver.phoneNumber || 'N/A'}</td>
                  <td>
                    {driver.driverProfile
                      ? getApprovalBadge(driver.driverProfile.approvalStatus)
                      : 'N/A'}
                  </td>
                  <td>{getStatusBadge(driver.status)}</td>
                  <td>
                    {driver.driverProfile?.rating ? (
                      <span>⭐ {driver.driverProfile.rating.toFixed(1)}</span>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{driver.driverProfile?.totalTrips || 0}</td>
                  <td>
                    {driver.driverProfile?.isOnline ? (
                      <CheckCircle size={20} className="icon-success" />
                    ) : (
                      <span className="text-muted">Offline</span>
                    )}
                  </td>
                  <td>{formatDate(driver.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => handleViewDriver(driver.id)}
                      className="btn-icon"
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="pagination">
        <div className="pagination-info">
          Showing {drivers.length} of {total} drivers
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn-icon"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="page-number">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="btn-icon"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default DriversPage;